import { useCallback, useEffect, useState } from 'react';
import type { Eu } from '../../acesso';
import { ROTULO_DO_ESTADO, dataCurta, lerMinhasAuditorias, numeroDoCaso } from '../../auditoria';
import type { CasoDaFila, ContaDaPessoa } from '../../auditoria';
import { EstadoVazio, ErroDeArea, SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { ContaInteira, Selo, SeloDoResultado, contaTexto } from './qualidade-comum';
import './qualidade.css';

/**
 * TELA 24 C — "Minhas auditorias": o que a PRÓPRIA pessoa vê.
 *
 * Só os casos dela dos últimos 30 dias e a própria conta, inteira ("3 ÷ 8 = 37,5%") ou "amostra
 * insuficiente". Nunca o nome de um colega, nunca ranking: não existe ranking de erros no produto.
 */
export function MinhasAuditorias(_p: { eu: Eu | null }) {
  const [d, setD] = useState<{
    casos: readonly CasoDaFila[];
    conta: ContaDaPessoa;
    retencaoLigada: boolean;
  } | null>(null);
  const [falha, setFalha] = useState<'erro' | 'sem-acesso' | null>(null);
  const carregar = useCallback(() => {
    setFalha(null);
    void lerMinhasAuditorias(30).then((r) => {
      if (r.ok) setD(r.dados);
      else setFalha(r.falha.tipo === 'sem-acesso' ? 'sem-acesso' : 'erro');
    });
  }, []);
  useEffect(carregar, [carregar]);

  if (falha === 'sem-acesso') return <SemAcesso area="Minhas auditorias" />;
  const c = d?.conta;
  return (
    <section className="ql-minhas">
      <div className="ql-minhas-cartao">
        <header className="ql-minhas-topo">
          <h1>Minhas auditorias</h1>
          <span>Apenas os seus casos · 30 dias</span>
        </header>
        {falha ? (
          <ErroDeArea oQue="as suas auditorias" aoTentar={carregar} />
        ) : !d ? (
          <div className="ql-carregando" aria-busy="true" />
        ) : d.casos.length === 0 ? (
          <EstadoVazio
            icone="shield"
            titulo="Nenhuma divergência sua nos últimos 30 dias"
            texto="Quando uma contagem sua virar caso de auditoria, ele aparece aqui com o resultado e o motivo."
          />
        ) : (
          <ul className="ql-minhas-lista">
            {d.casos.map((x) => (
              <li key={x.id}>
                <a href={`#/caso?id=${x.id}`}>
                  <div>
                    <span className="ql-minhas-num">
                      {numeroDoCaso(x.numero)} · {dataCurta(x.concluidaEm ?? x.abertoEm)}
                    </span>
                    <b>{x.produto}</b>
                  </div>
                  {x.resultado ? (
                    <SeloDoResultado r={x.resultado} />
                  ) : (
                    <Selo tom="neutro">{ROTULO_DO_ESTADO[x.estado]}</Selo>
                  )}
                  <Icone nome="chev-r" tamanho={14} traco={1.8} />
                </a>
              </li>
            ))}
          </ul>
        )}
        {c && c.bloqueio && d.retencaoLigada ? (
          <div className="ql-minhas-aviso">
            <b>Premiação em análise.</b> Sua conta na janela de avaliação passou do limite de
            qualidade do Giro ({contaTexto(c.erros, c.conclusivas, c.taxa)}, limite {c.threshold}
            %). Você pode ver cada caso, a evidência e o motivo. O Giro continua disponível.
          </div>
        ) : c && c.erros > 0 ? (
          <div className="ql-minhas-aviso">
            {c.erros === 1
              ? 'Um caso concluído como erro de contagem entrou'
              : `${String(c.erros)} casos concluídos como erro de contagem entraram`}{' '}
            na avaliação de qualidade. Você pode ver o caso, a evidência e o motivo.
          </div>
        ) : null}
      </div>
      {c && (
        <div className="ql-minhas-cartao ql-minhas-conta">
          <ContaInteira c={c} titulo="SUA CONTA NA JANELA DE AVALIAÇÃO" />
          <p className="ql-nota">
            {c.incluidos.length} auditoria{c.incluidos.length === 1 ? '' : 's'} conclusiva
            {c.incluidos.length === 1 ? '' : 's'} entr{c.incluidos.length === 1 ? 'ou' : 'aram'} na
            conta; {c.excluidos.length} caso{c.excluidos.length === 1 ? '' : 's'} ficou
            {c.excluidos.length === 1 ? '' : 'ram'} de fora (inconclusivo, problema de dados,
            produto em outro local ou reaberto) — esses nunca contam como erro.
          </p>
        </div>
      )}
      <p className="ql-nota ql-minhas-rodape">
        Transparência sobre os próprios casos, sem acesso ao desempenho dos colegas. Nenhum ranking
        de erros existe em lugar algum do produto.
      </p>
    </section>
  );
}
